import { COMPANY_ROLES } from 'constants/user';

export const DOCUMENT_CATEGORY_KEYS = {
  REGISTRATION: 'registration',
  INSURANCE: 'insurance',
  LICENSES: 'licenses',
  OTHER: 'other',
};

/** Все категории — по ним раскладываются документы, в том числе скрытые из интерфейса */
const ALL_DOCUMENT_CATEGORY_KEYS = [
  DOCUMENT_CATEGORY_KEYS.REGISTRATION,
  DOCUMENT_CATEGORY_KEYS.INSURANCE,
  DOCUMENT_CATEGORY_KEYS.LICENSES,
  DOCUMENT_CATEGORY_KEYS.OTHER,
];

/** Единственный документ, который нужен грузоотправителю */
export const REGISTRATION_DOCUMENT_TYPE = 'Свидетельство о государственной регистрации';

/** Документы экспедитора: сам он не перевозит, поэтому лицензий перевозчика с него не требуется */
export const FORWARDER_LICENSE_DOCUMENT_TYPE = 'Лицензия на услуги транспортно-экспедиционной деятельности';
export const FORWARDER_INSURANCE_DOCUMENT_TYPE = 'Страхование ответственности экспедитора';

/**
 * Категории, скрытые из интерфейса: «Другие документы» пока не показываем.
 * OTHER при этом остаётся корзиной для документов, не попавших в остальные категории,
 * поэтому убираем её только из вывода — чтобы вернуть, достаточно очистить этот список.
 */
export const HIDDEN_DOCUMENT_CATEGORIES = [DOCUMENT_CATEGORY_KEYS.OTHER];

const isHiddenCategory = (categoryKey) => HIDDEN_DOCUMENT_CATEGORIES.indexOf(categoryKey) !== -1;

/** Категории перевозчика и экспедитора: свидетельство о регистрации у них отдельным блоком не показываем */
const DEFAULT_DOCUMENT_CATEGORY_KEYS = [
  DOCUMENT_CATEGORY_KEYS.INSURANCE,
  DOCUMENT_CATEGORY_KEYS.LICENSES,
  DOCUMENT_CATEGORY_KEYS.OTHER,
];

export const DOCUMENT_CATEGORY_ORDER = DEFAULT_DOCUMENT_CATEGORY_KEYS.filter(
  (categoryKey) => !isHiddenCategory(categoryKey),
);

export const DOCUMENT_CATEGORY_LIMITS = {
  [DOCUMENT_CATEGORY_KEYS.INSURANCE]: 2,
  [DOCUMENT_CATEGORY_KEYS.LICENSES]: 2,
  [DOCUMENT_CATEGORY_KEYS.OTHER]: 2,
};

export const DOCUMENT_CATEGORY_REQUIRED_TYPES = {
  [DOCUMENT_CATEGORY_KEYS.INSURANCE]: [
    'Страхование ответственности автомобильного перевозчика',
    'Подтверждение оплаты страхового полиса',
  ],
  [DOCUMENT_CATEGORY_KEYS.LICENSES]: [
    'Лицензия на внутренний автомобильный транспорт',
    'Лицензия на международные автомобильные перевозки',
  ],
  [DOCUMENT_CATEGORY_KEYS.OTHER]: [
    'ОГРН',
    'ИНН компании',
  ],
};

/** Документы без срока действия — у них не спрашиваем и не показываем даты */
export const UPLOAD_ONLY_DOCUMENT_TYPES = new Set([
  'Подтверждение оплаты страхового полиса',
  REGISTRATION_DOCUMENT_TYPE,
]);

/** Набор документов перевозчика и экспедитора — страховки, лицензии и скрытая корзина «другое» */
const DEFAULT_DOCUMENT_PROFILE = {
  categoryOrder: DOCUMENT_CATEGORY_ORDER,
  requiredTypes: DOCUMENT_CATEGORY_REQUIRED_TYPES,
  limits: DOCUMENT_CATEGORY_LIMITS,
};

/**
 * Грузоотправитель ничего не перевозит сам, поэтому ни страховок, ни лицензий с него не требуется —
 * достаточно свидетельства о регистрации, и других документов у него быть не должно
 */
const SHIPPER_DOCUMENT_PROFILE = {
  categoryOrder: [DOCUMENT_CATEGORY_KEYS.REGISTRATION],
  requiredTypes: {
    [DOCUMENT_CATEGORY_KEYS.REGISTRATION]: [REGISTRATION_DOCUMENT_TYPE],
  },
  limits: {
    [DOCUMENT_CATEGORY_KEYS.REGISTRATION]: 1,
  },
};

/**
 * Набор документов экспедитора — лицензия на транспортно-экспедиционную деятельность и
 * страхование ответственности, по одному документу. Лицензия идёт первой: без неё
 * экспедиторская деятельность невозможна
 */
const FORWARDER_DOCUMENT_PROFILE = {
  categoryOrder: [DOCUMENT_CATEGORY_KEYS.LICENSES, DOCUMENT_CATEGORY_KEYS.INSURANCE],
  requiredTypes: {
    [DOCUMENT_CATEGORY_KEYS.LICENSES]: [FORWARDER_LICENSE_DOCUMENT_TYPE],
    [DOCUMENT_CATEGORY_KEYS.INSURANCE]: [FORWARDER_INSURANCE_DOCUMENT_TYPE],
  },
  limits: {
    [DOCUMENT_CATEGORY_KEYS.LICENSES]: 1,
    [DOCUMENT_CATEGORY_KEYS.INSURANCE]: 1,
  },
};

const matchesRole = (companyRole, role) => String(companyRole || '').trim().toLowerCase() === role.toLowerCase();

export const isShipperCompany = (companyRole) => matchesRole(companyRole, COMPANY_ROLES.SHIPPER);

export const isForwarderCompany = (companyRole) => matchesRole(companyRole, COMPANY_ROLES.FORWARDER);

/**
 * Набор документов зависит от роли компании: у грузоотправителя и экспедитора он свой.
 * @param {string} [companyRole] Значение `company_roles` компании
 */
export const getDocumentProfile = (companyRole) => {
  if (isShipperCompany(companyRole)) {
    return SHIPPER_DOCUMENT_PROFILE;
  }

  if (isForwarderCompany(companyRole)) {
    return FORWARDER_DOCUMENT_PROFILE;
  }

  return DEFAULT_DOCUMENT_PROFILE;
};

/** Типы документов, доступные роли: всё, что перечислено в её видимых категориях */
export const getProfileDocumentTypes = (companyRole) => {
  const { categoryOrder, requiredTypes } = getDocumentProfile(companyRole);

  return categoryOrder.reduce((types, categoryKey) => types.concat(requiredTypes[categoryKey] || []), []);
};

const DOCUMENT_LABEL_MAP = {
  'Лицензия ЕС на международные автомобильные перевозки':
    'Лицензия на международные автомобильные перевозки',
};

const EXACT_DOCUMENT_CATEGORY_MAP = {
  [REGISTRATION_DOCUMENT_TYPE]: DOCUMENT_CATEGORY_KEYS.REGISTRATION,
  // Документы экспедитора перечисляем явно, чтобы категория не зависела от формулировки названия
  [FORWARDER_LICENSE_DOCUMENT_TYPE]: DOCUMENT_CATEGORY_KEYS.LICENSES,
  [FORWARDER_INSURANCE_DOCUMENT_TYPE]: DOCUMENT_CATEGORY_KEYS.INSURANCE,
  'Страхование ответственности автомобильного перевозчика': DOCUMENT_CATEGORY_KEYS.INSURANCE,
  'Подтверждение оплаты страхового полиса': DOCUMENT_CATEGORY_KEYS.INSURANCE,
  'Лицензия на внутренний автомобильный транспорт': DOCUMENT_CATEGORY_KEYS.LICENSES,
  'Лицензия на международные автомобильные перевозки': DOCUMENT_CATEGORY_KEYS.LICENSES,
  'ОГРН': DOCUMENT_CATEGORY_KEYS.OTHER,
  'ИНН компании': DOCUMENT_CATEGORY_KEYS.OTHER,
};

export const getDocumentCategoryTitleKey = (categoryKey) => `documents.categories.${categoryKey}`;

export const getDocumentDisplayLabel = (value = '') => DOCUMENT_LABEL_MAP[value] || value;

export const resolveDocumentCategory = (value = '') => {
  const normalizedValue = getDocumentDisplayLabel(value);

  if (EXACT_DOCUMENT_CATEGORY_MAP[normalizedValue]) {
    return EXACT_DOCUMENT_CATEGORY_MAP[normalizedValue];
  }

  if (/^Свидетельство о (государственной )?регистрации/i.test(normalizedValue)) {
    return DOCUMENT_CATEGORY_KEYS.REGISTRATION;
  }

  if (/^Страхован/i.test(normalizedValue)) {
    return DOCUMENT_CATEGORY_KEYS.INSURANCE;
  }

  if (/^Лиценз/i.test(normalizedValue)) {
    return DOCUMENT_CATEGORY_KEYS.LICENSES;
  }

  return DOCUMENT_CATEGORY_KEYS.OTHER;
};

export const groupDocuments = (documents = [], { includeEmptyCategories = false, companyRole } = {}) => {
  const { categoryOrder } = getDocumentProfile(companyRole);
  const grouped = ALL_DOCUMENT_CATEGORY_KEYS.reduce((accumulator, categoryKey) => {
    accumulator[categoryKey] = [];
    return accumulator;
  }, {});

  documents.forEach((document) => {
    const sourceLabel = document.status || document.file_name || document.label || '';
    const displayLabel = getDocumentDisplayLabel(sourceLabel);
    const categoryKey = resolveDocumentCategory(sourceLabel);

    grouped[categoryKey].push({
      ...document,
      displayLabel,
      categoryKey,
    });
  });

  return categoryOrder
    .map((categoryKey) => ({
      categoryKey,
      titleKey: getDocumentCategoryTitleKey(categoryKey),
      items: grouped[categoryKey],
    }))
    .filter((section) => includeEmptyCategories || section.items.length > 0);
};

/**
 * Format a date value to dd.MM.yyyy string.
 * Returns null if the value is falsy or not a valid date.
 */
export const formatDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Check if a document is upload-only (no date/status display).
 */
export const isUploadOnlyDocument = (document) =>
  UPLOAD_ONLY_DOCUMENT_TYPES.has(document.displayLabel || document.sourceLabel || '');

/**
 * Determine the status tone and label for a document based on its valid_to date.
 * Returns { tone: 'success' | 'warning' | 'danger', label: string } or null.
 */
export const getDocumentStatus = (document, t) => {
  if (isUploadOnlyDocument(document)) return null;

  const validToRaw = document.valid_to || document.validTo;
  const validToDate = validToRaw ? new Date(validToRaw) : null;
  const validTo = formatDateValue(validToRaw);

  if (!validToDate || Number.isNaN(validToDate.getTime()) || !validTo) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  validToDate.setHours(0, 0, 0, 0);

  if (validToDate < today) {
    return { tone: 'danger', label: t('documents.expired_on', { date: validTo }) };
  }

  const oneMonthFromNow = new Date(today);
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

  if (validToDate <= oneMonthFromNow) {
    return { tone: 'warning', label: t('documents.expiring_on', { date: validTo }) };
  }

  return { tone: 'success', label: t('documents.valid_until', { date: validTo }) };
};

/**
 * Оставляет из списка типов документов только положенные роли. Фильтровать по категориям
 * недостаточно: лицензии и страховки есть и у перевозчика, и у экспедитора, но документы разные.
 * @param {Array<{ value: string, label: string }>} options Типы документов из справочника
 * @param {string} [companyRole] Значение `company_roles` компании
 */
export const filterDocumentTypeOptions = (options = [], companyRole) => {
  const allowedTypes = new Set(getProfileDocumentTypes(companyRole));

  return options.filter((option) => allowedTypes.has(getDocumentDisplayLabel(option.label)));
};

export const groupDocumentTypeOptions = (options = [], { companyRole } = {}) =>
  groupDocuments(
    options.map((option) => ({
      ...option,
      status: option.label || option.value,
    })),
    { companyRole },
  ).map((section) => ({
    label: section.titleKey,
    options: section.items.map(({ displayLabel, ...option }) => ({
      ...option,
      label: displayLabel,
    })),
  }));
